import { interpolate } from '@gig/magic-common';
import { USER_LOGGED_IN_UPDATED, LANGUAGE_UPDATED, MESSAGING_MARK_READ, MESSAGING_LIST, MESSAGING_UNREAD_LIST, MESSAGING_LOADED, LANGUAGE_QUERY, USER_LOGGED_IN_QUERY } from '@gig/magic-messages';
import { magic } from '@gig/magic-sdk';
import { notificationService, isHidden } from '@gig/magic-messaging';
import { unlocalize, MessageType, MessageStatus } from '@gig/magic-sdk/dist/adapter/messages/message';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
class MessagingService {
  constructor() {
    this.messages = [];
    this.messagesLoaded = false;
    this.registerSchemas();
    this.subscribeMessagingEvents();
    this.subscribeToMessageUpdates();
  }
  subscribeMessagingEvents() {
    window.eventBus.subscribe(USER_LOGGED_IN_UPDATED, () => __async(this, null, function* () {
      yield this.syncMessages();
    }), { replay: true });
    window.eventBus.subscribe(LANGUAGE_UPDATED, () => __async(this, null, function* () {
      yield this.syncMessages();
    }));
    window.commandBus.subscribe(MESSAGING_MARK_READ, (event) => {
      this.markAsRead(event.payload.id, event.payload.isPopOpen);
    });
  }
  registerQueries() {
    if (this.listQuery === void 0) {
      this.listQuery = window.queryBus.subscribe(MESSAGING_LIST, () => {
        if (!this.messagesLoaded) {
          return [];
        }
        return this.messages;
      });
    }
    if (this.unreadListQuery === void 0) {
      this.unreadListQuery = window.queryBus.subscribe(MESSAGING_UNREAD_LIST, () => {
        if (!this.messagesLoaded) {
          return [];
        }
        return this.getUnreadMessages();
      });
    }
  }
  unregisterQueries() {
    var _a, _b;
    (_a = this.listQuery) == null ? void 0 : _a.unsubscribe();
    this.listQuery = void 0;
    (_b = this.unreadListQuery) == null ? void 0 : _b.unsubscribe();
    this.unreadListQuery = void 0;
  }
  syncMessages() {
    return __async(this, null, function* () {
      this.unregisterQueries();
      let languageFetched;
      try {
        const [language, loggedIn] = yield Promise.all([
          this.queryLanguage(),
          this.queryIsLoggedIn()
        ]);
        if (!language || !loggedIn) {
          this.clearMessages();
          this.registerQueries();
        } else {
          languageFetched = language;
          yield this.fetchMessages(language);
        }
      } finally {
        if (languageFetched && (yield this.queryLanguage()) === languageFetched) {
          this.registerQueries();
        }
      }
    });
  }
  subscribeToMessageUpdates() {
    magic.events.message((msg) => __async(this, null, function* () {
      const language = yield this.queryLanguage();
      const message = unlocalize(msg, language);
      this.updateMessage(message);
      if (message.type !== MessageType.NOTIFICATION || !message.subject) {
        return;
      }
      const params = __spreadValues({}, magic.session.getUser());
      notificationService.show(interpolate(message.subject, params), {
        uniqueID: message.id
      });
    }));
  }
  fetchMessages(language) {
    return __async(this, null, function* () {
      this.publishMessagesLoaded(false);
      const messages = yield magic.methods.messages.get(language);
      this.messages = messages.sort((msgA, msgB) => {
        return new Date(msgB.created).getTime() - new Date(msgA.created).getTime();
      });
      this.publishMessagesLoaded(true);
    });
  }
  updateMessage(message) {
    const index = this.messages.findIndex(({ id }) => id === message.id);
    switch (message.status) {
      case MessageStatus.NEW:
        if (index > -1) {
          return;
        }
        this.messages.unshift(message);
        this.publishMessagesLoaded(true);
        break;
      case MessageStatus.DELETED:
      case MessageStatus.CANCELLED:
        this.messages.splice(index, 1);
        this.publishMessagesLoaded(true);
        break;
      case MessageStatus.READ:
        this.markAsRead(message.id);
        break;
    }
  }
  clearMessages() {
    this.messages = [];
    this.publishMessagesLoaded(true);
  }
  markAsRead(messageId, isPopOpen) {
    const messageToRead = this.messages.find(({ id }) => id === messageId);
    if (!messageToRead) {
      return;
    }
    messageToRead.status = messageToRead.once ? MessageStatus.DELETED : MessageStatus.READ;
    if (!isPopOpen) {
      this.publishMessagesLoaded(true);
    }
  }
  publishMessagesLoaded(payload) {
    this.messagesLoaded = payload;
    window.eventBus.publish(MESSAGING_LOADED, payload);
  }
  getUnreadMessages() {
    return this.messages.filter((message) => {
      return message.status === MessageStatus.NEW && message.type !== MessageType.NOTIFICATION && !isHidden(message);
    });
  }
  queryLanguage() {
    return __async(this, null, function* () {
      return yield window.queryBus.query(LANGUAGE_QUERY);
    });
  }
  queryIsLoggedIn() {
    return __async(this, null, function* () {
      return yield window.queryBus.query(USER_LOGGED_IN_QUERY);
    });
  }
  registerSchemas() {
    window.eventBus.register(MESSAGING_LOADED, {
      type: "boolean"
    });
    window.queryBus.register(MESSAGING_LIST, {
      type: "array"
    });
    window.queryBus.register(MESSAGING_UNREAD_LIST, {
      type: "array"
    });
    window.commandBus.register(MESSAGING_MARK_READ, {
      type: "object",
      properties: {
        id: {
          type: "string"
        },
        isPopOpen: {
          anyOf: [
            {
              type: "boolean"
            },
            {
              type: "null"
            }
          ]
        }
      }
    });
  }
}

export { MessagingService };
